"use client"

import { colors } from "@/ui/theme/colors"
import styled from "@emotion/styled"
import { Box, Tab, Tabs, Typography } from "@mui/material"
import React, { useEffect, useState } from "react"
import { BlockComponent } from "./componentLoader"
import { InvestmentData, TabData } from "./utils"

// Types
interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

interface TabLabelProps {
  title?: string
  text?: string
  index: number
}

interface DesktopViewProps {
  tabs: TabData[]
  value: number
  onChange: (event: React.SyntheticEvent, newValue: number) => void
  investmentData: InvestmentData
}

// Styled Components
const ContentWrapper = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: transparent;
  min-height: 600px;
  position: relative;
`

const TabContentContainer = styled(Box)`
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;
  &.visible {
    opacity: 1;
    visibility: visible;
  }
`

const StyledTabs = styled(Tabs)`
  margin-top: 32px;
  width: 100%;
  min-height: 120px;
  & .MuiTabs-indicator {
    background-color: ${colors.red};
    height: 2px;
    top: 0;
  }
  & .MuiTabs-flexContainer {
    gap: 20px;
    width: 100%;
    justify-content: space-between;
  }
  & .MuiTab-root {
    color: ${colors.white};
    opacity: 0.7;
    border-top: 1px solid ${colors.lightGray};
    padding-top: 16px;
    flex: 1;
    max-width: none;
    min-width: auto;
    min-height: 100px;
    &.Mui-selected {
      color: ${colors.white};
      opacity: 1;
      font-weight: bold;
      border-top: 1px solid ${colors.red};
    }
  }
`

const TabLabelBox = styled(Box)`
  text-align: center;
  min-height: 80px;
`

// JSX Component Bits
const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => {
  const isActive = value === index
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => setIsVisible(true), 50)
      return () => clearTimeout(timer)
    } else {
      setIsVisible(false)
    }
  }, [isActive])

  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      <TabContentContainer className={isVisible ? "visible" : ""}>
        {children}
      </TabContentContainer>
    </div>
  )
}

const TabLabel: React.FC<TabLabelProps> = ({ title, text, index }) => (
  <TabLabelBox>
    <Typography variant="h6">{title || `Tab ${index + 1}`}</Typography>
    <Typography variant="body2">{text || ""}</Typography>
  </TabLabelBox>
)

// Main Component
const DesktopView: React.FC<DesktopViewProps> = ({
  tabs,
  value,
  onChange,
  investmentData,
}) => {
  return (
    <>
      <ContentWrapper>
        {tabs?.map((tab, index) => (
          <TabPanel key={index} value={value} index={index}>
            {tab?.blockToRender && (
              <BlockComponent
                blockName={tab.blockToRender}
                data={tab}
                investmentData={investmentData}
              />
            )}
          </TabPanel>
        ))}
      </ContentWrapper>

      <StyledTabs
        value={value}
        onChange={onChange}
        centered
        aria-label="best invest tabs"
      >
        {tabs?.map((tab, index) => (
          <Tab
            key={index}
            label={
              <TabLabel
                title={tab?.rulerTitle}
                text={tab?.rulerText}
                index={index}
              />
            }
          />
        ))}
      </StyledTabs>
    </>
  )
}

export default DesktopView
